import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { levelStyle } from "@/lib/tcfLevel";
import { BookOpen, Headphones, ArrowRight, FileText, Clock, Lock, Crown, Sparkles, LineChart } from "lucide-react";

const SKILLS = [
  { key: "reading", icon: BookOpen, label: "Reading", fr: "Compréhension écrite", mins: 60 },
  { key: "listening", icon: Headphones, label: "Listening", fr: "Compréhension orale", mins: 35 },
];

export default function ExamBank() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ["reading", "listening"].includes(searchParams.get("skill")) ? searchParams.get("skill") : "reading";
  const [tab, setTabState] = useState(initialTab);
  const setTab = (t) => { setTabState(t); setSearchParams({ skill: t }, { replace: true }); };

  useEffect(() => {
    api.get("/exams")
      .then((r) => { setExams(r.data.exams || []); setHasAccess(!!r.data.has_exam_access); })
      .catch((e) => { if (e.response?.status === 403) setHasAccess(false); else console.error("exams", e); })
      .finally(() => setLoading(false));
  }, []);

  const bySkill = useMemo(() => exams.reduce((a, e) => { (a[e.skill] = a[e.skill] || []).push(e); return a; }, {}), [exams]);
  const list = bySkill[tab] || [];
  const activeMeta = SKILLS.find((s) => s.key === tab);

  return (
    <div data-testid="exam-bank-page">
      <h1 className="font-display text-4xl font-semibold flex items-center gap-3"><FileText className="h-8 w-8 text-primary" /> TCF Mocks</h1>
      <p className="text-muted-foreground mt-2">Real TCF Canada mock exams with instant official /699 scoring, answer review and a real-exam timer.</p>
      <Link to={`/exams/progress?skill=${tab}`} data-testid="exam-view-progress" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
        <LineChart className="h-4 w-4" /> View detailed breakdown
      </Link>

      {/* Upsell banner for free users */}
      {!hasAccess && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-2xl emerald-surface grain text-white p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="exam-premium-gate">
          <div className="h-12 w-12 rounded-2xl bg-white/15 grid place-items-center shrink-0"><Lock className="h-6 w-6" /></div>
          <div className="flex-1">
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Preview the TCF Mocks — go Premium to unlock</h2>
            <p className="text-white/85 mt-1 text-sm">All 80 real TCF Reading &amp; Listening mock exams (with transcripts) come with Premium — early-launch price <span className="font-semibold">$15 CAD/month</span> <span className="line-through opacity-70">$29</span>.</p>
          </div>
          <Link to="/premium" data-testid="exam-upgrade-btn" className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-white text-primary font-semibold shrink-0">
            <Crown className="h-4 w-4" /> Go Premium <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      )}

      {/* Tabs */}
      <div className="mt-6 inline-flex rounded-full bg-secondary p-1" data-testid="exam-tabs">
        {SKILLS.map((s) => {
          const Icon = s.icon;
          const active = tab === s.key;
          return (
            <button key={s.key} onClick={() => setTab(s.key)} data-testid={`tab-${s.key}`}
              className={`inline-flex items-center gap-2 px-4 sm:px-5 h-10 rounded-full text-sm font-semibold transition-colors ${active ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon className="h-4 w-4" /> {s.label}
              <span className={`text-xs ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>({(bySkill[s.key] || []).length})</span>
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-sm text-muted-foreground flex items-center gap-1.5">
        <Clock className="h-4 w-4 text-primary" /> {activeMeta.fr} · {activeMeta.mins} min in timed mode (or switch to untimed at start).
      </p>

      {loading ? (
        <p className="text-muted-foreground mt-6">Loading…</p>
      ) : list.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center" data-testid="exam-bank-empty">
          <p className="text-sm text-muted-foreground">No {activeMeta.label} papers published yet. Check back soon.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
          {list.map((e, i) => {
            const Icon = activeMeta.icon;
            if (e.locked) {
              return (
                <motion.div key={e.slug} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Link to="/premium" data-testid={`exam-locked-${e.slug}`}
                    className="group relative block rounded-2xl bg-secondary/60 border border-dashed border-border p-6 h-full hover:border-primary hover:shadow-md transition-all">
                    <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[11px] font-bold text-primary bg-accent px-2 py-1 rounded-full"><Crown className="h-3 w-3" /> Premium</div>
                    <div className="h-11 w-11 rounded-xl bg-muted grid place-items-center"><Lock className="h-5 w-5 text-muted-foreground" /></div>
                    <h3 className="mt-4 font-semibold text-lg leading-snug text-foreground/80">{e.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{e.num_questions} questions · {activeMeta.mins} min</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                      <Sparkles className="h-4 w-4" /> Unlock with Premium <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            }
            return (
              <motion.div key={e.slug} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link to={`/exams/${e.slug}`} data-testid={`exam-card-${e.slug}`}
                  className="group block rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-1 transition-all h-full">
                  <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center"><Icon className="h-5 w-5 text-white" /></div>
                  <h3 className="mt-4 font-semibold text-lg leading-snug">{e.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{e.num_questions} questions · {activeMeta.mins} min</p>
                  {e.progress && (
                    <div className="mt-3" data-testid={`exam-progress-${e.slug}`}>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-muted-foreground">Last score</span>
                        <span className={`font-bold px-2 py-0.5 rounded-full border text-[11px] ${levelStyle(e.progress.last_cefr).chip}`}>{e.progress.last_label}</span>
                      </div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden">
                        <div className={`h-full ${levelStyle(e.progress.last_cefr).bar}`} style={{ width: `${Math.round((e.progress.last_score_699 / 699) * 100)}%` }} />
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">{e.progress.last_score_699}/699 · {e.progress.attempts} attempt{e.progress.attempts > 1 ? "s" : ""}</div>
                    </div>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">{e.progress ? "Retake paper" : "Start paper"} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
